import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Box, Heading, Select, Alert, AlertIcon, Button, ChakraProvider, useToast, Flex, Spacer, Text } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useNavigate } from 'react-router-dom';

const AssignQuestionsToPaperSet = () => {
  const [paperSets, setPaperSets] = useState([]);
  const [selectedPaperSetId, setSelectedPaperSetId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [assignedQuestions, setAssignedQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [questionCategories, setQuestionCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [error, setError] = useState(null);
  const [numberOfQuestions, setNumberOfQuestions] = useState(0);
  const toast = useToast();
  const navigate = useNavigate();
  const sourceGridRef = useRef();
  const destinationGridRef = useRef();

  useEffect(() => {
    const fetchPaperSets = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet`);
        const activePaperSets = (response.data.$values || response.data).filter(ps => ps.isActive);
        setPaperSets(activePaperSets);
      } catch (error) {
        setError('Error fetching paper sets');
        console.error('Error fetching paper sets:', error);
      }
    };

    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Questions`);
        setQuestions(Array.isArray(response.data) ? response.data : response.data.$values || []);
      } catch (error) {
        setError('Error fetching questions');
        console.error('Error fetching questions:', error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
        setCategories(response.data.$values || response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    const fetchQuestionCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/QuestionCategories`);
        setQuestionCategories(response.data.$values || response.data);
      } catch (error) {
        console.error('Error fetching question categories:', error);
      }
    };

    fetchPaperSets();
    fetchQuestions();
    fetchCategories();
    fetchQuestionCategories();
  }, []);

  useEffect(() => {
    if (selectedPaperSetId) {
      const fetchAssignedQuestions = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet/${selectedPaperSetId}/questions`);
          const assignedQuestionIds = (response.data.$values || response.data).map(q => q.questionId);
          setAssignedQuestions(assignedQuestionIds);
          if (sourceGridRef.current) {
            sourceGridRef.current.api.forEachNode(node => {
              node.setSelected(assignedQuestionIds.includes(node.data.questionId));
            });
          }

          // Fetch selected paper set details
          const selectedSet = paperSets.find(ps => ps.paperSetId === parseInt(selectedPaperSetId));
          if (selectedSet) {
            setNumberOfQuestions(selectedSet.numberOfQuestions);
          } else {
            setNumberOfQuestions(0);
          }
        } catch (error) {
          setError('Error fetching assigned questions');
          console.error('Error fetching assigned questions:', error);
        }
      };

      fetchAssignedQuestions();
    }
  }, [selectedPaperSetId, paperSets]);

  const handlePaperSetChange = (e) => {
    setSelectedPaperSetId(e.target.value);
    setAssignedQuestions([]); // Clear assigned questions when paper set changes
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleQuestionSelection = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedIds = selectedNodes.map((node) => node.data.questionId);
    setAssignedQuestions(selectedIds);
  };

  const handleAddSelectedQuestions = () => {
    const selectedNodes = sourceGridRef.current.api.getSelectedNodes();
    const selectedQuestions = selectedNodes.map(node => node.data);
    setAssignedQuestions(prev => [...new Set([...prev, ...selectedQuestions.map(q => q.questionId)])]);
  };

  const handleRemoveSelectedQuestions = () => {
    const selectedNodes = destinationGridRef.current.api.getSelectedNodes();
    const selectedIds = selectedNodes.map((node) => node.data.questionId);
    setAssignedQuestions(prev => prev.filter(id => !selectedIds.includes(id)));
  };

  const handleSubmit = async () => {
    if (assignedQuestions.length > numberOfQuestions) {
      toast({
        title: 'Error',
        description: `You cannot assign more than ${numberOfQuestions} questions to this paper set.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/PaperSet/${selectedPaperSetId}/questions`,
        { questionIds: assignedQuestions }
      );
      toast({
        title: 'Success',
        description: response.data.message || 'Paper set updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate('/admin/view-questions-paperset'); // Navigate to the desired route
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      toast({
        title: 'Error updating paper set',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const gridStyle = useMemo(() => ({ width: '100%', height: '360px' }), []);

  const getCategoryNames = (questionId) => {
    const categoryIds = questionCategories
      .filter(qc => qc.questionId === questionId)
      .map(qc => qc.categoryId);

    return categories
      .filter(category => categoryIds.includes(category.categoryId))
      .map(category => category.categoryName)
      .join(', ');
  };

  const columnDefs = useMemo(() => [
    { headerCheckboxSelection: true, checkboxSelection: true, width: 50 },
    { headerName: 'Number', valueGetter: 'node.rowIndex + 1', width: 90 },
    { headerName: 'Question', field: 'questionText', flex: 1, filter: 'agTextColumnFilter', tooltipField: 'questionText' },
    { headerName: 'Category', field: 'categories', flex: 1, filter: 'agTextColumnFilter', valueGetter: params => getCategoryNames(params.data.questionId), tooltipValueGetter: params => getCategoryNames(params.data.questionId) },
    {
      headerName: 'Options',
      field: 'options',
      valueGetter: (params) => {
        const options = params.data.options.$values || params.data.options || [];
        return options.map(option => option.optionText).join(', ');
      },
      flex: 2,
      filter: 'agTextColumnFilter',
      tooltipValueGetter: (params) => {
        const options = params.data.options.$values || params.data.options || [];
        return options.map(option => `${option.optionText} (${option.isCorrect ? 'Correct' : 'Incorrect'})`).join(', ');
      }
    },
    {
      headerName: 'Correct Answer',
      field: 'correctAnswer',
      valueGetter: (params) => {
        const options = params.data.options.$values || params.data.options || [];
        const correctOption = options.find((option) => option.isCorrect);
        return correctOption ? correctOption.optionText : 'N/A';
      },
      flex: 1,
      filter: 'agTextColumnFilter',
      tooltipValueGetter: (params) => {
        const options = params.data.options.$values || params.data.options || [];
        const correctOption = options.find((option) => option.isCorrect);
        return correctOption ? correctOption.optionText : 'N/A';
      }
    },
  ], [questionCategories, categories]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: true,
    tooltipComponent: 'customTooltip'
  }), []);

  const availableQuestions = useMemo(() => {
    const filtered = selectedCategory === 'All Categories'
      ? questions
      : questions.filter(q => {
          const categoryNames = getCategoryNames(q.questionId);
          return categoryNames.includes(selectedCategory);
        });
    return filtered.filter(q => !assignedQuestions.includes(q.questionId));
  }, [questions, assignedQuestions, selectedCategory, questionCategories, categories]);

  const assignedQuestionsData = useMemo(() => {
    return questions.filter(q => assignedQuestions.includes(q.questionId));
  }, [questions, assignedQuestions]);

  return (
    <Box className="relative lg:w-[80%] md:w-3/4 w-full mx-auto mt-10">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-300 to-blue-600 shadow-lg transform -skew-y-12 sm:skew-y-0 sm:-rotate-[8deg] rounded-3xl drop-shadow-md"></div>

      <Box className="relative bg-white rounded-2xl drop-shadow-md p-6 flex flex-col mt-10 space-y-4 z-10">
        <div className="flex items-center gap-4 border-b pb-2 border-gray-300">
          <Heading size="lg" className="text-xl font-bold mb-4">Assign Questions to Paper Set</Heading>
        </div>

        {error && (
          <Alert status="error" mb={2}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Select placeholder="Select Paper Set" onChange={handlePaperSetChange} mb={4}>
          {paperSets.map((paperSet) => (
            <option key={paperSet.paperSetId} value={paperSet.paperSetId}>
              {paperSet.title}
            </option>
          ))}
        </Select>

        <Select placeholder="Select Category" onChange={handleCategoryChange} mb={4}>
          <option value="All Categories">All Categories</option>
          {categories.map((category) => (
            <option key={category.categoryId} value={category.categoryName}>
              {category.categoryName}
            </option>
          ))}
        </Select>

        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <Heading size="md">Source: Available Questions</Heading>
          <Text>Total Questions: {availableQuestions.length}</Text>
          <Button size="sm" colorScheme="blue" onClick={handleAddSelectedQuestions}>
            Add Selected Questions
          </Button>
        </Flex>

        <div className="ag-theme-alpine" style={gridStyle}>
          <AgGridReact
            ref={sourceGridRef}
            rowData={availableQuestions}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            pagination={true}
            paginationPageSize={10}
          />
        </div>

        <Flex justifyContent="space-between" alignItems="center" mt={4} mb={2}>
          <Heading size="md">Destination: Assigned Questions</Heading>
          <Text>Total Questions: {assignedQuestions.length} / {numberOfQuestions}</Text>
          <Button size="sm" colorScheme="red" onClick={handleRemoveSelectedQuestions}>
            Remove Selected Questions
          </Button>
        </Flex>

        <div className="ag-theme-alpine" style={gridStyle}>
          <AgGridReact
            ref={destinationGridRef}
            rowData={assignedQuestionsData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            pagination={true}
            paginationPageSize={10}
          />
        </div>

        <Button colorScheme="green" onClick={handleSubmit} mt={4} alignSelf="center" size="sm">
          Save Questions to Paper Set
        </Button>
      </Box>
    </Box>
  );
};

const App = () => (
  <ChakraProvider>
    <AssignQuestionsToPaperSet />
  </ChakraProvider>
);

export default App;
